<template>
    <!-- Main sidebar content -->
    <div class="relative flex-1 sm:px-6 h-auto">
        <div class="relative lg:pt-6">
            <!-- Connect-bank banner (MY1-3044): trackable, unconnected cards
                 only; dismissal is per institution and persists. -->
            <div
                v-if="connectBannerVisible"
                class="mt-4 rounded-lg border border-orange-200 dark:border-orange-400/30 bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/15 dark:to-amber-500/10 p-4 flex flex-col gap-3"
            >
                <div class="flex items-start gap-2">
                    <Icon
                        icon="bank-financial"
                        class="w-5 h-5 shrink-0 text-orange-500"
                    />
                    <div class="flex flex-col gap-0.5 min-w-0">
                        <p
                            class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                        >
                            Connect bank to auto-track bonuses
                        </p>
                        <p
                            class="text-sm font-normal leading-5 text-slate-600 dark:text-slate-300"
                        >
                            Add a bank connection to automatically track your
                            spending, benefits, and welcome bonuses.
                        </p>
                    </div>
                </div>
                <div class="flex items-center gap-4">
                    <button
                        type="button"
                        class="btn btn-orange !w-auto px-4 py-2 text-sm font-medium"
                        @click="openConnectFlow"
                    >
                        Connect bank
                    </button>
                    <button
                        type="button"
                        class="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 transition-colors"
                        @click="dismissConnectBanner"
                    >
                        Dismiss
                    </button>
                </div>
            </div>

            <div class="h-auto" aria-hidden="true">
                <div class="mt-6">
                    <div
                        class="flex flex-col items-start rounded-[8px] self-stretch"
                    >
                        <h4
                            v-if="selectedCard?.card"
                            class="flex items-center gap-3 self-stretch text-slate-800 dark:text-slate-100 text-base font-medium leading-6 mb-2"
                            style="font-style: normal; line-height: 24px"
                        >
                            Details
                        </h4>

                        <dl class="w-full">
                            <!-- 2. Status -->
                            <div
                                v-if="selectedCard?.card_status"
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="card-check" class="w-5 h-5" />
                                    Status
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-semibold flex items-center"
                                >
                                    <span
                                        v-if="
                                            selectedCard?.card_status === 'open'
                                        "
                                        class="px-3 py-0.5 bg-emerald-50 dark:bg-emerald-500/30 text-emerald-500 dark:text-emerald-200 text-xs font-medium rounded-xl h-5 flex items-center shrink-0"
                                    >
                                        Open
                                    </span>
                                    <span
                                        v-if="
                                            selectedCard?.card_status ===
                                            'closed'
                                        "
                                        class="px-3 py-0.5 bg-red-50 dark:bg-red-500/30 text-red-500 dark:text-red-200 text-xs font-medium rounded-xl h-5 flex items-center shrink-0"
                                    >
                                        Closed
                                    </span>
                                </dd>
                            </div>
                            <!-- Bank -->
                            <div
                                v-if="
                                    selectedCard?.card?.issuing_bank?.name ||
                                    selectedCard?.issuingBank?.name
                                "
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon
                                        icon="bank-financial"
                                        class="w-5 h-5"
                                    />
                                    Bank
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center gap-2 min-w-0 overflow-hidden"
                                >
                                    <ImageLoader>
                                        <template #image>
                                            <div
                                                class="w-4 h-4 flex items-center justify-center overflow-hidden rounded-full bg-white dark:bg-slate-800 shrink-0"
                                            >
                                                <img
                                                    :src="
                                                        selectedCard
                                                            ?.institution
                                                            ?.small_logo_url
                                                    "
                                                    class="max-w-[80%] max-h-[80%] rounded-full"
                                                    :alt="
                                                        selectedCard
                                                            ?.institution
                                                            ?.name ||
                                                        'Institution logo'
                                                    "
                                                />
                                            </div>
                                        </template>
                                        <template #error>
                                            <div
                                                class="w-4 h-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0"
                                            >
                                                <Icon
                                                    icon="bank-financial"
                                                    class="h-4 w-4"
                                                />
                                            </div>
                                        </template>
                                    </ImageLoader>
                                    <span class="truncate">
                                        {{
                                            selectedCard?.card?.issuing_bank
                                                ?.name ||
                                            selectedCard?.issuingBank?.name
                                        }}
                                    </span>
                                </dd>
                            </div>
                            <!-- 3. Card Type -->
                            <div
                                v-if="
                                    selectedCard?.card_status &&
                                    selectedCard?.card_status !== '-'
                                "
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="credit-card" class="w-5 h-5" />
                                    Card Type
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                                >
                                    {{
                                        selectedCard?.card?.type === 'personal'
                                            ? 'Personal'
                                            : 'Business'
                                    }}
                                </dd>
                            </div>
                            <!-- 4. Account Type -->
                            <div
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                                v-if="selectedCard?.account_type"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="account-type" class="w-5 h-5" />
                                    Account Type
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                                >
                                    {{
                                        selectedCard?.account_type ===
                                        'authorized_user'
                                            ? 'Authorized User'
                                            : 'Primary Cardholder'
                                    }}

                                    <span
                                        class="inline-flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 !text-slate-800 dark:text-slate-100 text-xs w-6 h-6 ml-1"
                                        v-if="
                                            selectedCard?.account_type ===
                                            'authorized_user'
                                        "
                                    >
                                        <i
                                            class="fas fa-user-friends !text-slate-600 dark:text-slate-300 fa-sm"
                                        ></i>
                                    </span>
                                </dd>
                            </div>
                            <!-- Date Opened -->
                            <div
                                v-if="selectedCard?.date_opened"
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="calendar" class="w-5 h-5" />
                                    Date Opened
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                                >
                                    {{ selectedCard.date_opened }}
                                </dd>
                            </div>
                            <!-- Annual fee -->
                            <div
                                v-if="selectedCard?.card"
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="dollar-coin" class="w-5 h-5" />
                                    Annual Fee
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center"
                                    v-if="
                                        !selectedCard?.user_managed_annual_fee
                                    "
                                >
                                    <!-- Initial annual fee active -->
                                    <span
                                        v-if="
                                            selectedCard?.card &&
                                            selectedCard?.card
                                                .initial_annual_fee !==
                                                undefined &&
                                            selectedCard?.card
                                                .initial_annual_fee !== null &&
                                            selectedCard?.initial_annual_fee_active
                                        "
                                    >
                                        <span
                                            class="text-red-400 line-through block"
                                        >
                                            <span class="text-slate-500 dark:text-slate-400">
                                                ${{
                                                    selectedCard.card.annual_fee
                                                }}
                                            </span>
                                        </span>
                                        <span class="block">
                                            <span>
                                                ${{
                                                    selectedCard.card
                                                        .initial_annual_fee
                                                }}
                                            </span>
                                            for your first year, then
                                            <span>
                                                ${{
                                                    selectedCard.card.annual_fee
                                                }}
                                            </span>
                                        </span>
                                    </span>

                                    <!-- Regular annual fee -->
                                    <div
                                        class="text-slate-800 dark:text-slate-100 text-sm leading-none flex items-center"
                                        v-if="
                                            selectedCard?.card &&
                                            selectedCard?.card.annual_fee !==
                                                undefined &&
                                            selectedCard?.card.annual_fee !==
                                                null &&
                                            !selectedCard?.initial_annual_fee_active
                                        "
                                    >
                                        ${{ selectedCard?.card.annual_fee }}
                                        <span
                                            class="text-sm font-normal ml-2 block text-slate-500 dark:text-slate-400"
                                            v-if="
                                                selectedCard?.card &&
                                                selectedCard?.date_opened &&
                                                selectedCard?.annual_fee_due_date &&
                                                selectedCard?.card.annual_fee >
                                                    0
                                            "
                                        >
                                            (Due:
                                            {{
                                                selectedCard.annual_fee_due_date
                                                    .date
                                            }})
                                        </span>
                                    </div>
                                    <span
                                        class="inline-block rounded-full bg-slate-400 text-white text-center relative annual-fee-label-trigger"
                                        style="width: 22px; height: 22px"
                                        v-if="
                                            selectedCard?.card &&
                                            selectedCard?.card.annual_fee_label
                                        "
                                    >
                                        <span
                                            class="annual-fee-label font-semibold text-sm leading-none"
                                        >
                                            {{
                                                selectedCard?.card
                                                    .annual_fee_label
                                            }}
                                        </span>
                                        <i class="fas fa-exclamation"></i>
                                    </span>
                                </dd>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal flex items-center0 text-slate-800 dark:text-slate-100"
                                    v-if="selectedCard?.user_managed_annual_fee"
                                >
                                    <span
                                        class="font-normal text-sm leading-none"
                                        :class="{
                                            '!text-gray-400 line-through':
                                                selectedCard?.card_status ===
                                                'closed',
                                        }"
                                    >
                                        ${{ selectedCard?.annual_fee }}
                                    </span>
                                    <span
                                        class="text-xs font-normal ml-2 block"
                                        v-if="
                                            selectedCard?.card &&
                                            selectedCard?.date_opened &&
                                            selectedCard?.annual_fee > 0 &&
                                            selectedCard?.annual_fee_due_date &&
                                            selectedCard?.card_status !==
                                                'closed'
                                        "
                                    >
                                        (Due:
                                        {{
                                            selectedCard.annual_fee_due_date
                                                .date
                                        }})
                                    </span>
                                </dd>
                            </div>
                            <!-- Credit Limit -->
                            <div
                                v-if="
                                    selectedCard?.credit_limit !== null &&
                                    selectedCard?.credit_limit !== undefined
                                "
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="credit-limit" class="w-5 h-5" />
                                    Credit Limit
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-normal leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                                >
                                    ${{ formattedCreditLimit }}
                                </dd>
                            </div>
                            <div
                                v-if="
                                    selectedCard?.contributes_to_wallet_five_twenty_four &&
                                    selectedCard?.five_twenty_four_level
                                "
                                class="py-2 grid grid-cols-[3fr_5fr] gap-1 items-center h-9"
                            >
                                <dt
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 flex items-center gap-2"
                                >
                                    <Icon icon="credit-cards" class="w-5 h-5" />
                                    5/24 Status
                                </dt>
                                <dd
                                    class="text-sm text-left sm:mt-0 font-medium leading-5 text-slate-800 dark:text-slate-100 flex items-center"
                                >
                                    <span
                                        class="px-3 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-medium rounded-xl h-5 flex items-center shrink-0"
                                    >
                                        {{
                                            selectedCard.five_twenty_four_level
                                        }}/24
                                    </span>
                                </dd>
                            </div>
                        </dl>
                        <section
                            class="w-full mt-6"
                            v-if="
                                selectedCard?.welcome_bonus &&
                                !selectedCard?.product_changed
                            "
                        >
                            <h4
                                class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-4"
                            >
                                Welcome bonus
                            </h4>
                            <BonusCard
                                :bonus="selectedCard?.welcome_bonus"
                                bonusType="welcome_bonus"
                                :bonusTiers="selectedCard?.welcome_bonus?.tiers"
                                :program="selectedCard?.card?.program"
                                :action-busy="bonusActionBusy"
                                @mark-received="openMarkReceived"
                                @goal-not-met="confirmGoalNotMet"
                                @connect-bank="openConnectFlow"
                                :can-enable-spending="viewerOwnsSelectedCard"
                                :spending-owner-name="selectedCardOwnerName"
                                @enable-spending="enableSpending"
                            />
                        </section>
                        <section
                            class="w-full mt-6"
                            v-if="selectedCard?.bonus_offers?.length"
                        >
                            <h4
                                class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-4"
                            >
                                Bonus offers
                            </h4>
                            <BonusCard
                                v-for="bonusOffer in selectedCard?.bonus_offers"
                                :key="bonusOffer.id"
                                :bonus="bonusOffer"
                                bonusType="bonus_offer"
                            />
                        </section>
                    </div>
                    <ProductChangeDetails
                        v-if="
                            selectedCard?.product_changed ||
                            (selectedCard?.product_changes &&
                                selectedCard.product_changes.length > 0)
                        "
                        :activeAccount="selectedCard"
                        class="w-full mt-6"
                        @add-product-change="handleAddProductChange"
                        @edit-product-change="handleEditProductChange"
                        @delete-product-change="handleDeleteProductChange"
                    />

                    <!-- Bonus Spending Categories Section - Only show if RewardsCC data exists -->
                    <div class="mb-6 mt-6">
                        <h4
                            class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-2"
                            v-if="displayedBonusCategories.length > 0"
                        >
                            Bonus Spending Categories
                        </h4>
                        <dl
                            class="mb-6"
                            v-if="displayedBonusCategories.length > 0"
                        >
                            <div
                                v-for="(
                                    category, index
                                ) in displayedBonusCategories"
                                :key="index"
                                class="py-3"
                                :class="{
                                    'fade-bottom':
                                        !showAllBonusCategories &&
                                        index === 4 &&
                                        cardRewardsData.spend_bonus_categories
                                            .length > 5,
                                }"
                            >
                                <div class="flex items-start">
                                    <div
                                        class="flex justify-center items-center w-9 py-0.5 px-0 rounded bg-slate-200 dark:bg-slate-700 dark:text-slate-300 text-center font-medium text-sm leading-5 mr-3 shrink-0"
                                    >
                                        {{ category.earnMultiplier
                                        }}{{
                                            category.rate_type === 'percentage'
                                                ? '%'
                                                : 'x'
                                        }}
                                    </div>
                                    <div class="flex-1">
                                        <dt
                                            v-if="
                                                category.spendBonusCategoryName
                                            "
                                            class="text-sm text-slate-800 dark:text-slate-100 font-semibold leading-5 mb-1"
                                        >
                                            {{
                                                category.spendBonusCategoryName
                                            }}
                                        </dt>
                                        <dd
                                            class="text-sm font-normal leading-5 text-gray-700 dark:text-gray-200"
                                        >
                                            {{
                                                formatBonusDescription(category)
                                            }}
                                        </dd>
                                        <div
                                            v-if="
                                                category.isSpendLimit &&
                                                category.spendLimit > 0
                                            "
                                            class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                                        >
                                            <span>
                                                Up to ${{
                                                    this.formatNumber(
                                                        category.spendLimit
                                                    )
                                                }}
                                                <span
                                                    v-if="
                                                        category.spendLimitResetPeriod
                                                    "
                                                >
                                                    per
                                                    {{
                                                        category.spendLimitResetPeriod.toLowerCase()
                                                    }}
                                                </span>
                                            </span>
                                        </div>
                                        <div
                                            v-if="
                                                category.isDateLimit &&
                                                category.limitBeginDate &&
                                                category.limitEndDate
                                            "
                                            class="text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                                        >
                                            Valid from
                                            {{
                                                formatDate(
                                                    category.limitBeginDate
                                                )
                                            }}
                                            to
                                            {{
                                                formatDate(
                                                    category.limitEndDate
                                                )
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-if="
                                    cardRewardsData.spend_bonus_categories
                                        .length > 5
                                "
                                class="mt-2 text-left"
                            >
                                <button
                                    @click="toggleBonusCategories"
                                    class="btn-base btn-transparent"
                                    id="hs-wallet-show-bonus-categories"
                                >
                                    {{
                                        showAllBonusCategories
                                            ? 'Show less'
                                            : 'Show all'
                                    }}
                                </button>
                            </div>
                        </dl>
                        <div
                            v-if="
                                rewardsccData?.base_spend_amount &&
                                selectedCard?.card?.earn_rates_data_source ===
                                    'rewardscc'
                            "
                        >
                            <h5
                                class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mt-8 mb-2"
                            >
                                Base Earn Rate
                            </h5>
                            <div class="flex items-start">
                                <div class="shrink-0 mr-3">
                                    <Icon
                                        icon="checkmark"
                                        :width="20"
                                        :height="20"
                                    />
                                </div>
                                <div class="flex-1">
                                    <p
                                        class="text-sm font-normal leading-5 text-gray-700 dark:text-gray-200"
                                    >
                                        {{ rewardsccData?.base_spend_amount }}X
                                        {{
                                            rewardsccData?.base_spend_earn_currency
                                        }}
                                        on all other purchases
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Card Benefits Section -->
                        <CardBenefitsList
                            :benefits="selectedCard?.card?.card_benefits || []"
                            :benefits-summary="selectedCard?.benefits_summary"
                            :connected-account-id="selectedCard?.id"
                            :is-subscribed="walletFreemium.isSubscribed"
                            :tracked-connected-account-ids="
                                walletFreemium.trackedConnectedAccountIds
                            "
                        />

                        <!-- Card Timeline Section -->
                        <div class="mb-6" v-if="sortedTimelineEvents.length">
                            <h4
                                class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-2"
                            >
                                Changes history
                            </h4>
                            <div class="relative pt-2">
                                <div class="space-y-4">
                                    <template
                                        v-for="(
                                            event, index
                                        ) in sortedTimelineEvents"
                                        :key="event.type"
                                    >
                                        <div class="relative pl-8">
                                            <!-- :class="{
                                            'pb-4':
                                                index ===
                                                sortedTimelineEvents.length - 1,
                                            'pb-6': index < sortedTimelineEvents.length - 1,
                                        }" -->
                                            <div
                                                class="absolute left-[calc(0.5rem-0.125rem)] top-2 w-1 h-1 rounded-full bg-slate-500 shadow-sm"
                                            ></div>
                                            <div
                                                v-if="
                                                    index <
                                                    sortedTimelineEvents.length -
                                                        1
                                                "
                                                class="absolute left-[calc(0.5rem-0.0625rem)] top-[calc(0.5rem+0.4rem)] w-0.5 h-[calc(100%+0.4rem)] bg-slate-200"
                                            ></div>
                                            <div>
                                                <h5
                                                    class="text-sm font-medium text-slate-800 dark:text-slate-100"
                                                >
                                                    {{ event.title }}
                                                </h5>
                                                <p
                                                    class="text-sm text-slate-500 dark:text-slate-400"
                                                >
                                                    {{ formatDate(event.date) }}
                                                </p>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mark-received date popover (MY1-3043, desktop) -->
        <teleport to="body">
            <div
                v-if="showMarkReceivedPopover"
                class="fixed inset-0 z-[10000]"
                @click.self="closeMarkReceived"
            >
                <div
                    ref="markReceivedPopover"
                    class="absolute bg-white dark:bg-slate-800 rounded-lg border flex flex-col items-end border-slate-200 dark:border-slate-700 shadow dark:shadow-slate-900/50 w-80 p-4"
                    :style="markReceivedPopoverStyle"
                >
                    <div class="mb-4 w-full">
                        <UiInput
                            id="bonus-date-received"
                            name="bonus_date_received"
                            label="Date bonus received"
                            placeholder="Date bonus received"
                            :options="{ icon: 'calendar' }"
                        >
                            <template v-slot:input>
                                <FlatPickr
                                    :config="markReceivedDateConfig"
                                    name="bonus_date_received"
                                    :modelValue="markReceivedDate"
                                    id="bonus-date-received"
                                    class="relative block w-full rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                    placeholder="Date bonus received"
                                    @on-change="
                                        (dates) => onMarkReceivedDateChange(dates)
                                    "
                                />
                            </template>
                        </UiInput>
                        <p
                            v-if="markReceivedError"
                            class="mt-2 text-sm text-red-600"
                        >
                            {{ markReceivedError }}
                        </p>
                    </div>

                    <button
                        @click="submitMarkReceived"
                        :disabled="bonusActionBusy"
                        class="btn btn-primary !w-40 flex items-center gap-2"
                    >
                        <Icon icon="check-lg" />
                        {{ bonusActionBusy ? 'Saving...' : 'Mark received' }}
                    </button>
                </div>
            </div>
        </teleport>

        <!-- Mark-received sheet (MY1-3043, mobile) -->
        <Sheet
            :open="showMarkReceivedSheet"
            title="Mark bonus as received"
            size="xs"
            @update:open="showMarkReceivedSheet = $event"
        >
            <div class="w-full mb-6">
                <UiInput
                    id="bonus-date-received-m"
                    name="bonus_date_received_m"
                    label="Date bonus received"
                    placeholder="Date bonus received"
                    :options="{ icon: 'calendar' }"
                >
                    <template v-slot:input>
                        <FlatPickr
                            :config="markReceivedDateConfig"
                            name="bonus_date_received_m"
                            :modelValue="markReceivedDate"
                            id="bonus-date-received-m"
                            class="relative block w-full rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden bg-white dark:bg-slate-800 py-1.5 pl-10 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                            placeholder="Date bonus received"
                            @on-change="(dates) => onMarkReceivedDateChange(dates)"
                        />
                    </template>
                </UiInput>
                <p v-if="markReceivedError" class="mt-2 text-sm text-red-600">
                    {{ markReceivedError }}
                </p>
            </div>

            <div class="flex justify-between gap-2">
                <button @click="closeMarkReceived" class="btn btn-secondary !w-40">
                    Cancel
                </button>

                <button
                    @click="submitMarkReceived"
                    :disabled="bonusActionBusy"
                    class="btn btn-primary flex items-center gap-2 !w-auto"
                >
                    <Icon icon="check-lg" />
                    {{ bonusActionBusy ? 'Saving...' : 'Mark received' }}
                </button>
            </div>
        </Sheet>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import { usePage } from '@inertiajs/vue3';
import dayjs from 'dayjs';
import { notify } from 'notiwind';
import { useWalletStore } from '@/store/wallet';
import { Apii } from '@/services/api.js';
import BonusCard from '@/components/wallet/partials/bonus-card.vue';
import CardBenefitsList from '@/components/wallet/partials/card-benefits-list.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ProductChangeDetails from '@/components/wallet/partials/product-change-details.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import Sheet from '@/components/ui/sheet/sheet.vue';

const ApiService = new Apii();

export default {
    props: {
        user: { type: Object },
    },
    components: {
        Icon,
        ProductChangeDetails,
        ImageLoader,
        BonusCard,
        CardBenefitsList,
        UiInput,
        FlatPickr,
        Sheet,
    },
    data() {
        return {
            showAllBonusCategories: false,
            rewardsccData: null,
            // MY1-3044 — session-local dismissals so the banner hides
            // immediately; the persisted list arrives with the next page load.
            locallyDismissedInstitutions: [],
            // MY1-3043 — mark-received / goal-not-met actions
            bonusActionBusy: false,
            showMarkReceivedPopover: false,
            showMarkReceivedSheet: false,
            markReceivedTierId: null,
            markReceivedDate: '',
            markReceivedError: '',
            markReceivedPopoverStyle: {},
            markReceivedDateConfig: {
                dateFormat: 'm/d/Y',
                maxDate: 'today',
                minDate: null,
                disableMobile: 'true',
            },
        };
    },
    computed: {
        /**
         * MY1-3077 — who owns the card on screen. Same resolution the bonus
         * actions use (sendBonusUpdate): the owner prop wins, then the card's
         * embedded owner, then the signed-in user when neither is passed.
         */
        selectedCardOwnerUuid() {
            return (
                this.user?.uuid ||
                this.selectedCard?.user?.uuid ||
                usePage().props.userData?.uuid ||
                null
            );
        },

        /**
         * A shared wallet shows cards belonging to other members, and the
         * tracking state is derived from the *card owner's* My Spending
         * preference (WalletService passes spendingEnabledByUserId keyed on
         * account.user_id). /spending/enable only mutates the signed-in user,
         * so on someone else's card the opt-in cannot clear that card's prompt
         * and would silently change the viewer's own preference instead.
         * Offer it only on the viewer's own cards (PR #1703 review).
         */
        /**
         * MY1-3077 — names the owner in the "Not tracked" note shown on
         * someone else's card. `User::$visible` is {id, uuid, name}, so wallet
         * users carry a name; the child falls back to generic phrasing without
         * one.
         */
        selectedCardOwnerName() {
            return this.user?.name || this.selectedCard?.user?.name || null;
        },

        viewerOwnsSelectedCard() {
            const viewerUuid = usePage().props.userData?.uuid || null;

            return Boolean(
                viewerUuid && this.selectedCardOwnerUuid === viewerUuid
            );
        },

        sortedTimelineEvents() {
            const events = [];

            // Card opened - use original card info if product changed
            if (this.selectedCard?.date_opened) {
                let cardName = this.selectedCard.card?.title || 'Card';
                let openedDate = this.selectedCard.date_opened;

                // If there are multiple product changes, use the oldest one's previous_card
                if (
                    this.selectedCard.product_changes &&
                    this.selectedCard.product_changes.length > 0
                ) {
                    // Find the oldest product change (earliest product_changed_date)
                    const oldestChange = [
                        ...this.selectedCard.product_changes,
                    ].sort(
                        (a, b) =>
                            new Date(a.product_changed_date) -
                            new Date(b.product_changed_date)
                    )[0];

                    if (oldestChange?.previous_card) {
                        cardName = oldestChange.previous_card.title || 'Card';
                    }
                    if (oldestChange?.product_changed_date_opened) {
                        openedDate = oldestChange.product_changed_date_opened;
                    }
                } else if (
                    this.selectedCard.product_changed &&
                    this.selectedCard.original_card
                ) {
                    // Fallback for legacy single product change
                    cardName = this.selectedCard.original_card.title || 'Card';
                    if (this.selectedCard.product_changed_date_opened) {
                        openedDate =
                            this.selectedCard.product_changed_date_opened;
                    }
                }

                events.push({
                    type: 'card_opened',
                    title: `${cardName} opened`,
                    date: openedDate,
                });
            }

            // Product changes - iterate through all product_changes
            if (
                this.selectedCard?.product_changes &&
                this.selectedCard.product_changes.length > 0
            ) {
                // Sort product changes by date (oldest first) to determine the chain
                const sortedChanges = [
                    ...this.selectedCard.product_changes,
                ].sort(
                    (a, b) =>
                        new Date(a.product_changed_date) -
                        new Date(b.product_changed_date)
                );

                sortedChanges.forEach((change, index) => {
                    if (change.product_changed_date) {
                        // Determine what card they changed TO
                        // For the last change (most recent), they changed to the current card
                        // For earlier changes, they changed to the next change's previous_card
                        let changedToCard;
                        if (index === sortedChanges.length - 1) {
                            // Most recent change - changed to current card
                            changedToCard =
                                this.selectedCard.card?.title || 'Card';
                        } else {
                            // Earlier change - changed to the next change's previous card
                            changedToCard =
                                sortedChanges[index + 1]?.previous_card
                                    ?.title || 'Card';
                        }

                        events.push({
                            type: `product_changed_${change.id}`,
                            title: `Product changed to ${changedToCard}`,
                            date: change.product_changed_date,
                        });
                    }
                });
            } else if (
                this.selectedCard?.product_changed &&
                this.selectedCard?.product_changed_date
            ) {
                // Fallback for legacy single product change
                const currentCardName = this.selectedCard.card?.title || 'Card';
                events.push({
                    type: 'product_changed',
                    title: `Product changed to ${currentCardName}`,
                    date: this.selectedCard.product_changed_date,
                });
            }

            // Welcome bonus added - use welcomeBonus relationship
            const wb = this.selectedCard?.welcome_bonus;
            if (wb?.bonus_amount && wb?.minimum_spend_deadline) {
                const bonusType = wb.type || 'points';
                const bonusLabel =
                    bonusType === 'cash_back'
                        ? `$${wb.bonus_amount.toLocaleString()}`
                        : bonusType === 'free_nights'
                            ? `${wb.bonus_amount} nights`
                            : `${wb.bonus_amount.toLocaleString()} points`;
                events.push({
                    type: 'bonus_added',
                    title: `${this.formatBonusType(bonusType)} welcome bonus added (${bonusLabel})`,
                    date: wb.minimum_spend_deadline,
                });
            }

            // Welcome bonus received - use welcomeBonus relationship.
            // Tiered bonuses track received state per-tier, not on the parent record.
            if (wb?.received && wb?.date_received && wb?.type !== 'tiered') {
                const bonusType = wb.type || 'points';
                const amount = wb.bonus_amount ?? 0;
                const bonusLabel =
                    bonusType === 'cash_back'
                        ? `$${amount.toLocaleString()}`
                        : bonusType === 'free_nights'
                            ? `${amount} nights`
                            : `${amount.toLocaleString()} points`;
                events.push({
                    type: 'bonus_received',
                    title: `${this.formatBonusType(bonusType)} welcome bonus received (${bonusLabel})`,
                    date: wb.date_received,
                });
            }

            // Tiered welcome bonus — emit one event per received tier
            if (wb?.type === 'tiered' && wb?.tiers?.length > 0) {
                wb.tiers.forEach((tier) => {
                    if (tier.received && tier.date_received) {
                        const amount = tier.bonus_amount ?? 0;
                        const bonusUnits = tier.bonus_units || 'points';
                        const bonusLabel = bonusUnits === 'cash_back' || bonusUnits === 'cashback' || bonusUnits === 'dollars'
                            ? `$${amount.toLocaleString()}`
                            : bonusUnits === 'free_nights'
                                ? `${amount} nights`
                                : `${amount.toLocaleString()} points`;
                        events.push({
                            type: `bonus_received_tier_${tier.tier_number}`,
                            title: `Welcome bonus tier ${tier.tier_number} received (${bonusLabel})`,
                            date: tier.date_received,
                        });
                    }
                });
            }

            // Card closed
            if (
                this.selectedCard?.card_status === 'closed' &&
                this.selectedCard?.date_closed
            ) {
                events.push({
                    type: 'card_closed',
                    title: 'Card closed',
                    date: this.selectedCard.date_closed,
                });
            }

            const validEvents = events.filter((event) => event.date);

            return validEvents.sort(
                (a, b) => new Date(b.date) - new Date(a.date)
            );
        },

        ...mapState(useWalletStore, [
            'selectedCard',
            'wallet',
            'cdnUrl',
            'features',
        ]),

        walletFreemium() {
            return {
                isSubscribed: this.wallet?.freemium?.is_subscribed ?? true,
                trackedConnectedAccountIds:
                    this.wallet?.freemium?.tracked_connected_account_ids ?? [],
            };
        },

        // MY1-3044 — the connect-bank banner: only for cards whose issuer
        // supports a connection (issuing_bank.institution_code, the same key
        // that gates trackability) that aren't connected, until the user
        // dismisses that institution.
        cardInstitutionCode() {
            return this.selectedCard?.card?.issuing_bank?.institution_code || null;
        },

        cardIsConnected() {
            return Boolean(
                this.selectedCard?.member_guid &&
                    !this.selectedCard?.converted_to_manual
            );
        },

        connectBannerVisible() {
            if (!this.cardInstitutionCode || this.cardIsConnected) {
                return false;
            }

            // Auto-tracking is a My Spending feature (my-spending flag).
            if (!usePage().props.userData?.spending_available) {
                return false;
            }

            // MY1-3046: never pitch a connection for a card the user product-
            // changed away from or already closed.
            if (
                this.selectedCard?.product_changed ||
                this.selectedCard?.card_status !== 'open'
            ) {
                return false;
            }

            if (this.locallyDismissedInstitutions.includes(this.cardInstitutionCode)) {
                return false;
            }

            const dismissed =
                usePage().props.userData?.dismissed_connect_institutions || {};

            return !(this.cardInstitutionCode in dismissed);
        },

        hasFeatureFlag() {
            return (flagName) => {
                if (
                    this.wallet &&
                    this.wallet.features &&
                    typeof this.wallet.features[flagName] === 'boolean'
                ) {
                    return this.wallet.features[flagName];
                }

                if (
                    this.features &&
                    typeof this.features[flagName] === 'boolean'
                ) {
                    return this.features[flagName];
                }

                return false;
            };
        },

        cardRewardsData() {
            // Try to use the data from the rewardsccData property
            if (this.rewardsccData) {
                return this.rewardsccData;
            }

            // Next, try to use data from the card object
            if (
                this.selectedCard &&
                this.selectedCard.card &&
                this.selectedCard.card.rewardscc_data
            ) {
                return this.selectedCard.card.rewardscc_data;
            }

            // If no data available, return empty structure
            return {
                spend_bonus_categories: [],
            };
        },

        displayedBonusCategories() {
            if (
                !this.cardRewardsData ||
                !this.cardRewardsData.spend_bonus_categories
            ) {
                return [];
            }

            return this.showAllBonusCategories
                ? this.cardRewardsData.spend_bonus_categories
                : this.cardRewardsData.spend_bonus_categories.slice(0, 5);
        },

        formattedCreditLimit() {
            if (
                !this.selectedCard ||
                this.selectedCard.credit_limit === null ||
                this.selectedCard.credit_limit === undefined
            ) {
                return '';
            }

            // Handle zero values explicitly
            if (
                this.selectedCard.credit_limit === 0 ||
                this.selectedCard.credit_limit === '0.00'
            ) {
                return '0';
            }

            // Use formatThousand for non-zero values
            return this.$filters.formatThousand(
                Math.trunc(this.selectedCard.credit_limit)
            );
        },
    },
    watch: {
        selectedCard: {
            immediate: true,
            handler(newCard) {
                this.showAllBonusCategories = false;

                if (newCard && newCard.card && newCard.card.rewardscc_data) {
                    this.rewardsccData = newCard.card.rewardscc_data;
                } else {
                    this.rewardsccData = null;
                }
            },
        },
    },

    methods: {
        // MY1-3044 — both the banner button and the widget tip enter the
        // existing MX connect flow, opened by the wallet page via the store.
        openConnectFlow() {
            useWalletStore().requestConnectModal();
        },

        /**
         * MY1-3077 — the MY1-3045 prompt only shows on a card that is already
         * bank-connected, so the My Spending opt-in is the one thing missing.
         * Turn it on from here instead of navigating to /spending. The
         * endpoint dispatches the recompute and flushes the tracking caches,
         * so refreshing the wallet swaps the prompt for a progress bar — at
         * $0 until the queued figures land.
         */
        async enableSpending() {
            // Guard the action as well as the button: /spending/enable only
            // ever mutates the signed-in user, so firing it for someone else's
            // card would opt the viewer in and leave that card untouched.
            if (!this.viewerOwnsSelectedCard) {
                return;
            }

            this.bonusActionBusy = true;

            try {
                await window.axios.post('/spending/enable');
            } catch (error) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text:
                            error?.response?.data?.message ||
                            'Failed to turn on My Spending',
                    },
                    4000
                );

                return;
            } finally {
                this.bonusActionBusy = false;
            }

            // Past this point the preference is committed, so a refresh
            // failure is a stale-view problem, not a failed opt-in — saying
            // otherwise contradicts the server (PR #1703 review).
            notify(
                {
                    group: 'success',
                    title: 'Success',
                    text: 'My Spending is on — we\'ll track this bonus automatically',
                },
                4000
            );

            try {
                await this.refreshBonusSurfaces();
            } catch (error) {
                notify(
                    {
                        group: 'warn',
                        title: 'Heads up',
                        text: 'My Spending is on, but this page could not refresh. Reload to see progress.',
                    },
                    6000
                );
            }
        },

        async dismissConnectBanner() {
            const code = this.cardInstitutionCode;
            if (!code) return;

            this.locallyDismissedInstitutions.push(code);

            try {
                await window.axios.post(
                    `/connect-institutions/${encodeURIComponent(code)}/dismiss`
                );
            } catch (error) {
                // Keep it hidden for the session; persistence retries on the
                // next dismissal.
                console.error('Failed to persist banner dismissal', error);
            }
        },

        /**
         * MY1-3043 — welcome-bonus actions. The strip (or the sidebar header
         * menu) opens the date picker for mark-received, or confirms a missed
         * goal directly; both PUT the wallet-scoped welcome-bonus endpoint
         * (tier_id targets a single tier — SUPPORT-745) and then refresh the
         * wallet payload so every surface re-renders from fresh tracking.
         */
        openMarkReceived({ tierId = null, event = null } = {}) {
            const bonus = this.selectedCard?.welcome_bonus;
            if (!bonus) return;

            this.markReceivedTierId = tierId;
            this.markReceivedError = '';
            this.markReceivedDate = dayjs().format('MM/DD/YYYY');
            this.markReceivedDateConfig.minDate = this.selectedCard?.date_opened
                ? dayjs(this.selectedCard.date_opened).format('MM/DD/YYYY')
                : null;

            if (window.innerWidth < 769) {
                this.showMarkReceivedSheet = true;
                return;
            }

            this.showMarkReceivedPopover = true;
            this.$nextTick(() => {
                const popover = this.$refs.markReceivedPopover;
                const trigger = event?.currentTarget || event?.target;

                if (popover && trigger?.getBoundingClientRect) {
                    const triggerRect = trigger.getBoundingClientRect();
                    const popoverRect = popover.getBoundingClientRect();
                    const top = Math.min(
                        triggerRect.bottom + 8,
                        window.innerHeight - popoverRect.height - 16
                    );
                    const left = Math.max(
                        16,
                        Math.min(
                            triggerRect.left,
                            window.innerWidth - popoverRect.width - 16
                        )
                    );
                    this.markReceivedPopoverStyle = {
                        top: `${top}px`,
                        left: `${left}px`,
                    };
                } else {
                    // Opened from the header menu — no anchor; center it.
                    this.markReceivedPopoverStyle = {
                        top: '30%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                    };
                }
            });
        },

        closeMarkReceived() {
            this.showMarkReceivedPopover = false;
            this.showMarkReceivedSheet = false;
            this.markReceivedTierId = null;
            this.markReceivedDate = '';
            this.markReceivedError = '';
        },

        onMarkReceivedDateChange(dates) {
            this.markReceivedDate = dates?.[0] ?? '';
        },

        async submitMarkReceived() {
            if (!this.markReceivedDate) {
                this.markReceivedError = 'Please select a date';
                return;
            }
            if (dayjs(this.markReceivedDate).isAfter(dayjs())) {
                this.markReceivedError = 'Date cannot be in the future';
                return;
            }

            await this.sendBonusUpdate(
                {
                    received: true,
                    date_received: dayjs(this.markReceivedDate).format('YYYY-MM-DD'),
                    tier_id: this.markReceivedTierId,
                },
                'Bonus marked as received'
            );
        },

        async confirmGoalNotMet({ tierId = null } = {}) {
            await this.sendBonusUpdate(
                { goal_not_met: true, tier_id: tierId },
                'Bonus tracking dismissed'
            );
        },

        // Header-menu entry points (called by account-sidebar via ref).
        openMarkReceivedFromMenu() {
            const bonus = this.selectedCard?.welcome_bonus;
            if (!bonus) return;

            const tiers = bonus.tiers || [];
            const activeTier =
                tiers.find((tier) => tier.tracking?.is_active_tier) ||
                tiers.find((tier) => !tier.received) ||
                null;

            this.openMarkReceived({ tierId: activeTier?.id ?? null });
        },

        async unmarkReceived() {
            const bonus = this.selectedCard?.welcome_bonus;
            if (!bonus) return;

            // For tiered bonuses, un-mark the most recently received tier.
            const receivedTiers = (bonus.tiers || []).filter(
                (tier) => tier.received
            );
            const lastReceived = receivedTiers.length
                ? [...receivedTiers].sort(
                    (a, b) => (b.tier_number ?? 0) - (a.tier_number ?? 0)
                )[0]
                : null;

            await this.sendBonusUpdate(
                { received: false, tier_id: lastReceived?.id ?? null },
                'Marked as received removed'
            );
        },

        async sendBonusUpdate(payload, successMessage) {
            const bonus = this.selectedCard?.welcome_bonus;
            // The endpoint's {userUuid} is an existence gate — the bonus
            // lookup itself is wallet-scoped — so any wallet member works;
            // fall back to the signed-in user when no owner prop is passed.
            const userUuid =
                this.user?.uuid ||
                this.selectedCard?.user?.uuid ||
                usePage().props.userData?.uuid ||
                null;

            if (!bonus || !this.wallet?.id || !userUuid) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Failed to update the welcome bonus',
                    },
                    4000
                );
                return;
            }

            this.bonusActionBusy = true;
            try {
                await ApiService.apiCall(
                    `/wallets/${this.wallet.id}/users/${userUuid}/welcome-bonus/${bonus.id}`,
                    'put',
                    payload
                );

                notify(
                    { group: 'success', title: 'Success', text: successMessage },
                    4000
                );

                this.closeMarkReceived();
                await this.refreshBonusSurfaces();
            } catch (error) {
                // Surface server validation (e.g. confirming a missed goal
                // before the deadline) instead of a generic failure.
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text:
                            error?.response?.data?.message ||
                            'Failed to update the welcome bonus',
                    },
                    4000
                );
            } finally {
                this.bonusActionBusy = false;
            }
        },

        // Refetch the wallet payload and re-point the panel at the fresh
        // account object so the widget, table row and pills all update.
        async refreshBonusSurfaces() {
            const walletStore = useWalletStore();
            const accountId = this.selectedCard?.id;

            await walletStore.getWalletByEntities(null, { background: true });

            const fresh = walletStore.walletCards?.find(
                (card) => card.id === accountId
            );
            if (fresh) {
                walletStore.setSelectedCard(fresh);
            }
        },

        handleCardImageError() {
            if (this.selectedCard && this.selectedCard.card) {
                this.selectedCard.card.image = false;
            }
        },

        formatBonusDescription(category) {
            if (category.spendBonusDesc) {
                return category.spendBonusDesc;
            }

            let desc = '';

            desc = `Earn ${category.earnMultiplier}${category.rate_type === 'percentage' ? '%' : 'x'} on ${category.spendBonusCategoryName}`;

            if (category.isSpendLimit && category.spendLimit > 0) {
                desc += ` on up to $${this.formatNumber(category.spendLimit)}`;

                if (category.spendLimitResetPeriod) {
                    desc += ` per ${category.spendLimitResetPeriod.toLowerCase()}`;
                }
            }

            return desc;
        },

        formatNumber(number) {
            return new Intl.NumberFormat('en-US').format(number);
        },

        formatBonusType(type) {
            const typeMap = {
                points: 'Points',
                cash_back: 'Cash back',
                free_nights: 'Free nights',
                tiered: 'Tiered',
                other: 'Other',
            };
            return typeMap[type] || 'Points';
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        },

        toggleBonusCategories() {
            this.showAllBonusCategories = !this.showAllBonusCategories;
        },

        formatCurrency(amount) {
            if (!amount) return '$0';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        },

        handleAddProductChange() {
            this.$emit('add-product-change', this.selectedCard);
        },

        handleEditProductChange(productChange) {
            this.$emit('edit-product-change', this.selectedCard, productChange);
        },

        handleDeleteProductChange(productChangeId) {
            this.$emit(
                'delete-product-change',
                this.selectedCard,
                productChangeId
            );
        },
    },
};
</script>
